/* eslint-disable no-prototype-builtins */
import { Timestamp } from "firebase/firestore";
import { MatchType } from "enums/matchType";
import ScoreToWin from "enums/scoreToWin";
import WagerType from "enums/wagerType";
import MatchPlayType from "enums/matchPlayType";
import { calculateNetScore } from "../index";

/**
 * Finds user index in tee time users array
 * by firebase uid or phone number
 *
 * @param {*} userData user data with uid and phone
 * @param {*} users array of users
 * @returns index in users array
 */
function findUserIndexInTeeTimeUsers(userData, users) {
  if (userData && users) {
    return users.findIndex(
      (user) => user.uid === userData.uid || user.phone === userData.phone,
    );
  }
  return -1;
}

/**
 * Finds user index in teams array
 * by firebase uid or phone number
 *
 * @param {*} userData user data with uid and phone
 * @param {*} teams array of teams
 * @returns indexes in teams and users array
 */
function findUserIndexInTeams(userData, teams) {
  if (userData && teams) {
    const teamIndex = teams.findIndex((team) =>
      team.users.some(
        (user) => user.uid === userData.uid || user.phone === userData.phone,
      ),
    );

    if (teamIndex !== -1) {
      const userIndex = teams[teamIndex].users.findIndex(
        (user) => user.uid === userData.uid || user.phone === userData.phone,
      );

      return { teamIndex, userIndex };
    }
  }
  return { teamIndex: -1, userIndex: -1 };
}

/**
 * Checks user in tee time users array
 * by firebase uid or phone number
 *
 * @param {*} userData user data with uid and phone
 * @param {*} users array of users
 * @returns true if arrays contains the user
 */
function isUserInTeeTimeUsers(userData, users, teams) {
  if (teams) {
    const { userIndex } = findUserIndexInTeams(userData, teams);
    return userIndex > -1;
  }
  return findUserIndexInTeeTimeUsers(userData, users) > -1;
}

/**
 * Checks user in tee times collections
 * by firebase uid or phone number
 *
 * @param {*} userData user data with uid and phone
 * @param {*} teeTimes array of teeTime
 * @returns true if tee times contain the user
 */
function isUserInTeeTimes(userData, teeTimes) {
  return (
    teeTimes?.findIndex((teeTime) =>
      isUserInTeeTimeUsers(userData, teeTime.users),
    ) > -1
  );
}

/**
 * It becomes possible to view tee time details if
 *  - 2 hours before start
 *  - number of users is two or more @deprecated
 *
 * @param {*} teeTime tee time
 * @returns true if active
 */
function isTeeTimeActive(teeTime) {
  const twoHoursInSeconds = 2 * 60 * 60;
  const { teeTime: time } = teeTime;
  return Timestamp.now().seconds - time.seconds > -twoHoursInSeconds;
}

function findTeeTimeByUser(teeTimes, user) {
  return teeTimes?.find(
    (teeTime) => findUserIndexInTeeTimeUsers(user, teeTime.users) !== -1,
  );
}

function calculatePlaces(matchType, stake, users, firstPlace, secondPlace) {
  const places = {};

  users.forEach(({ total }) => {
    const { [total]: place } = places;
    if (place) {
      const { count } = place;
      places[total] = { count: count + 1 };
    } else {
      places[total] = { count: 1 };
    }
  });

  const keys = Object.keys(places).sort((a, b) =>
    matchType === MatchType.Quota ? b - a : a - b,
  );

  if (keys?.length === 1) {
    return users.map((player) => ({
      ...player,
      place: 1,
      win: stake,
      net: 0,
      bets: [
        ...(player.bets || []),
        {
          win: stake,
          net: 0,
          stake,
        },
      ],
    }));
  }

  let firstTies = false;

  keys.forEach((total, index) => {
    if (!index && places[total].count > 1) {
      firstTies = true;
    }
    places[total] = { ...places[total], place: index + 1 };
  });

  return users.map((player) => {
    const { total } = player;
    const {
      [total]: { count, place },
    } = places;

    let win = 0;
    if (place === 1) {
      win =
        count > 1
          ? (stake * users.length) / count
          : (stake * users.length * firstPlace) / 100;
    } else if (place === 2 && !firstTies) {
      win = (stake * users.length * secondPlace) / (count * 100);
    }
    const net = win - stake;

    return {
      ...player,
      place,
      win,
      net,
      bets: [
        ...(player.bets || []),
        {
          win,
          net,
          stake,
        },
      ],
    };
  });
}

function calculatePayouts(allUsers) {
  const users = allUsers.map(({ send, receive, ...u }) => ({
    ...u,
    net: u.bets.reduce((prev, curr) => prev + curr.net, 0),
  }));

  const receive = users
    .map((u, i) => ({
      ...u,
      i,
    }))
    .filter((u) => u.net > 0);
  const send = users
    .map((u, i) => ({
      ...u,
      i,
    }))
    .filter((u) => u.net < 0);

  // eslint-disable-next-line no-restricted-syntax
  for (const s of send) {
    let sendAmount = Math.abs(s.net);
    // eslint-disable-next-line no-restricted-syntax
    for (const r of receive) {
      const { net: receiveAmount } = r;
      if (receiveAmount !== 0) {
        if (sendAmount - receiveAmount > 0) {
          users[s.i].send = [
            ...(users[s.i].send || []),
            {
              amount: receiveAmount,
              displayName: r.displayName,
              courseHandicap: r.courseHandicap,
              phone: r.phone,
              uid: r.uid,
            },
          ];
          users[r.i].receive = [
            ...(users[r.i].receive || []),
            {
              amount: receiveAmount,
              displayName: s.displayName,
              courseHandicap: s.courseHandicap,
              phone: s.phone,
              uid: s.uid,
            },
          ];
          sendAmount -= receiveAmount;
          r.net = 0;
        } else if (sendAmount - receiveAmount < 0) {
          users[s.i].send = [
            ...(users[s.i].send || []),
            {
              amount: sendAmount,
              displayName: r.displayName,
              courseHandicap: r.courseHandicap,
              phone: r.phone,
              uid: r.uid,
            },
          ];
          users[r.i].receive = [
            ...(users[r.i].receive || []),
            {
              amount: sendAmount,
              displayName: s.displayName,
              courseHandicap: s.courseHandicap,
              phone: s.phone,
              uid: s.uid,
            },
          ];
          r.net = receiveAmount - sendAmount;
          break;
        } else {
          users[s.i].send = [
            ...(users[s.i].send || []),
            {
              amount: sendAmount,
              displayName: r.displayName,
              courseHandicap: r.courseHandicap,
              phone: r.phone,
              uid: r.uid,
            },
          ];
          users[r.i].receive = [
            ...(users[r.i].receive || []),
            {
              amount: sendAmount,
              displayName: s.displayName,
              courseHandicap: s.courseHandicap,
              phone: s.phone,
              uid: s.uid,
            },
          ];
          r.net = 0;
          break;
        }
      }
    }
  }
  return users;
}

function calculateMatchPlay(holeResults, matchType, pressLimit, pressType) {
  let firstHole = 0;
  let lastHole = 17;
  let holesRemaining = 9;
  let press = false;
  let matchWithoutPress = false;
  let gameType = "big18";
  if (matchType === MatchPlayType.Front9) {
    lastHole = 8;
    gameType = "front9";
  } else if (matchType === MatchPlayType.Back9) {
    firstHole = 9;
    gameType = "back9";
  } else {
    holesRemaining = 18;
  }
  let pressHoles = { firstHole, holesRemaining };

  const result = {
    score: 0,
    margin: 0,
    hole: 0,
    winByScore: false,
    presses: [],
  };

  const holes = [...holeResults];

  do {
    ({ firstHole, holesRemaining } = pressHoles);
    let score = 0;
    let winByScore = false;
    press = false;

    for (let i = firstHole; i <= lastHole; i += 1) {
      // eslint-disable-next-line prefer-destructuring
      const { holeResult } = holeResults[i] ?? {};
      if (holeResult === "W") {
        score += 1;
      } else if (holeResult === "L") {
        score -= 1;
      }

      if (holeResult && holes[i][gameType]) {
        if (matchWithoutPress) {
          if (holes[i][gameType].presses) {
            holes[i][gameType].presses.push({ score });
          } else {
            holes[i][gameType].presses = [{ score }];
          }
        } else {
          holes[i][gameType].score = score;
        }
      }

      holesRemaining -= 1;

      if (!winByScore && Math.abs(score) - holesRemaining > 0 && holeResult) {
        if (score > 0) {
          result.score += 1;
        } else {
          result.score -= 1;
        }
        if (matchWithoutPress) {
          result.presses.push({ hole: i + 1, margin: score });
        } else {
          result.winByScore = true;
          result.margin = score;
          result.hole = i + 1;
        }
        winByScore = true;
      } else if (
        !winByScore &&
        holeResult &&
        !holesRemaining &&
        matchWithoutPress
      ) {
        result.presses.push({ hole: 0, margin: 0 });
      }

      if (
        matchType !== MatchPlayType.Big18 &&
        pressType > 1 &&
        Math.abs(score) === pressType &&
        !press
      ) {
        if (!(pressLimit && matchWithoutPress)) {
          press = true;
          pressHoles = { firstHole: i + 1, holesRemaining };
          if (holes[i][gameType]) {
            if (holes[i][gameType].presses) {
              holes[i][gameType].presses.push({ score: 0 });
            } else {
              holes[i][gameType].presses = [{ score: 0 }];
            }
          }
        }
      }
    }
    matchWithoutPress = true;
  } while (press);

  return { holes, result };
}

function calculateMatchPlayForSingleTeeTime(
  competitors,
  matchType,
  stake,
  pressLimit,
  pressType,
) {
  const scoreType =
    matchType === MatchType.MatchPlayGross ? "score" : "netScore";
  const result = [...competitors];
  for (let i = 0; i < result.length; i += 1) {
    result[i].matches = [];
    result[i].ways = 0;
    result[i].net = 0;
    for (let j = 0; j < result.length; j += 1) {
      if (
        (result[i].phone && result[i].phone !== result[j].phone) ||
        (result[i].teamId && result[i].teamId !== result[j].teamId)
      ) {
        let match = result[j].phone
          ? {
              phone: result[j].phone,
              displayName: result[j].displayName,
              uid: result[j].uid,
              holes: [],
            }
          : {
              teamId: result[j].teamId,
              name: result[j].name,
              holes: [],
            };
        let holes = [];
        for (let k = 0; k < 18; k += 1) {
          if (
            result[i].holes[k] &&
            result[j].holes[k] &&
            result[i].holes[k][scoreType] !== undefined &&
            result[j].holes[k][scoreType] !== undefined
          ) {
            if (result[i].holes[k][scoreType] < result[j].holes[k][scoreType]) {
              holes.push({
                back9: {},
                big18: {},
                front9: {},
                holeResult: "W",
              });
            } else if (
              result[i].holes[k][scoreType] > result[j].holes[k][scoreType]
            ) {
              holes.push({
                back9: {},
                big18: {},
                front9: {},
                holeResult: "L",
              });
            } else {
              holes.push({
                back9: {},
                big18: {},
                front9: {},
                holeResult: "T",
              });
            }
          }
        }
        let back9;
        let front9;
        let big18;
        // eslint-disable-next-line prefer-const
        ({ holes, result: back9 } = calculateMatchPlay(
          holes,
          MatchPlayType.Back9,
          pressLimit,
          pressType,
        ));
        // eslint-disable-next-line prefer-const
        ({ holes, result: front9 } = calculateMatchPlay(
          holes,
          MatchPlayType.Front9,
          pressLimit,
          pressType,
        ));
        // eslint-disable-next-line prefer-const
        ({ holes, result: big18 } = calculateMatchPlay(
          holes,
          MatchPlayType.Big18,
          pressLimit,
          pressType,
        ));
        match = { ...match, back9, front9, big18, holes };
        result[i].matches.push(match);
        const ways = back9.score + front9.score + big18.score;
        const net = ways * stake;
        result[i].ways += ways;
        result[i].net += net;
        result[i].bets = [{ net: result[i].net, stake }];
      }
    }
  }
  return result;
}

function calculateMatchesForIndividuals(
  users,
  matchType,
  stake,
  scoreToWin,
  carryOver,
  wagerType,
  firstPlace,
  secondPlace,
  pressLimit,
  pressType,
) {
  let allUsers = [...users];
  let gameStake = 0;
  const skins = [];
  switch (matchType) {
    case MatchType.StrokePlayNet:
    case MatchType.StrokePlayGross: {
      const scoreType =
        matchType === MatchType.StrokePlayGross
          ? "maxGrossScore"
          : "maxNetScore";
      allUsers = allUsers.map((u) => {
        const outScore = u.holes.reduce(
          (acc, h, i) => (i < 9 ? acc + h[scoreType] : acc),
          0,
        );
        const inScore = u.holes.reduce(
          (acc, h, i) => (i > 8 ? acc + h[scoreType] : acc),
          0,
        );
        const total = outScore + inScore;
        return {
          ...u,
          out: outScore,
          in: inScore,
          total,
        };
      });
      break;
    }
    case MatchType.Quota:
      allUsers = allUsers.map((u) => {
        const total = u.holes.reduce((acc, h) => acc + h.quota, 0) - u.quota;
        return { ...u, total };
      });
      break;
    case MatchType.SkinsGross:
    case MatchType.SkinsNet: {
      const scoreType =
        matchType === MatchType.SkinsGross ? "score" : "netScore";
      let totalWin = 0;
      for (let i = 0; i < 18; i += 1) {
        skins.push({ scores: [] });
        for (let j = 0; j < allUsers?.length; j += 1) {
          if (!i) {
            allUsers[j].win = 0;
            allUsers[j].winCount = 0;
          }
          skins[i].scores.push(allUsers[j].holes[i][scoreType]);
        }
        const scores = [...skins[i].scores];
        scores.sort((a, b) => a - b);
        const min1 = scores[0];
        const min2 = scores[1];
        const diff = min1 - min2;
        const birdieOrBetter = min1 - allUsers[0].holes[i].holePar < 0;
        const parOrBetter = min1 - allUsers[0].holes[i].holePar < 1;
        let cumulative = 1;
        switch (scoreToWin) {
          case ScoreToWin.LowScore:
            if (carryOver && i && !skins[i - 1].diff) {
              cumulative += skins[i - 1].skinsWon;
            }
            if (diff < 0) {
              totalWin += cumulative;
            }
            break;
          case ScoreToWin.MaxPar:
            if (
              carryOver &&
              i &&
              !(skins[i - 1].diff && skins[i - 1].parOrBetter)
            ) {
              cumulative += skins[i - 1].skinsWon;
            }
            if (diff < 0 && parOrBetter) {
              totalWin += cumulative;
            }
            break;
          case ScoreToWin.MaxBirdie:
            if (
              carryOver &&
              i &&
              !(skins[i - 1].diff && skins[i - 1].birdieOrBetter)
            ) {
              cumulative += skins[i - 1].skinsWon;
            }
            if (diff < 0 && birdieOrBetter) {
              totalWin += cumulative;
            }
            break;
          default:
        }

        skins[i] = {
          ...skins[i],
          min1,
          min2,
          diff,
          skinsWon: cumulative,
          birdieOrBetter,
          parOrBetter,
        };
      }

      const allUserLength = allUsers?.length;
      const perHole =
        wagerType === WagerType.Pot
          ? (stake * allUserLength) / totalWin
          : stake * allUserLength;
      for (let i = 0; i < 18; i += 1) {
        let cumulative = perHole;
        switch (scoreToWin) {
          case ScoreToWin.LowScore:
            if (carryOver && i && !skins[i - 1].diff) {
              cumulative += skins[i - 1].cumulative;
            }
            break;
          case ScoreToWin.MaxPar:
            if (
              carryOver &&
              i &&
              !(skins[i - 1].diff && skins[i - 1].parOrBetter)
            ) {
              cumulative += skins[i - 1].cumulative;
            }
            break;
          case ScoreToWin.MaxBirdie:
            if (
              carryOver &&
              i &&
              !(skins[i - 1].diff && skins[i - 1].birdieOrBetter)
            ) {
              cumulative += skins[i - 1].cumulative;
            }
            break;
          default:
        }
        skins[i] = {
          ...skins[i],
          perHole,
          cumulative,
        };
        for (let j = 0; j < allUsers?.length; j += 1) {
          let win = 0;
          switch (scoreToWin) {
            case ScoreToWin.LowScore:
              win =
                allUsers[j].holes[i][scoreType] < skins[i].min2
                  ? cumulative
                  : 0;
              break;
            case ScoreToWin.MaxPar:
              win =
                allUsers[j].holes[i][scoreType] < skins[i].min2 &&
                skins[i].parOrBetter
                  ? cumulative
                  : 0;
              break;
            case ScoreToWin.MaxBirdie:
              win =
                allUsers[j].holes[i][scoreType] < skins[i].min2 &&
                skins[i].birdieOrBetter
                  ? cumulative
                  : 0;
              break;
            default:
          }
          allUsers[j].holes[i].win = win;
          allUsers[j].win += win;
          allUsers[j].winCount += win ? skins[i].skinsWon : 0;
        }
      }
      let wager = 0;
      if (carryOver) {
        wager =
          (18 -
            (skins[17].diff < 0
              ? 0
              : skins[17].cumulative / skins[17].perHole)) *
          stake;
      } else {
        wager = totalWin * stake;
      }
      if (wagerType === WagerType.Pot) {
        wager = stake;
      }
      allUsers = allUsers.map((u) => ({
        ...u,
        wager,
        net: Math.round(u.win - wager),
        bets: [
          ...(u.bets || []),
          {
            net: Math.round(u.win - wager),
            win: u.win,
            stake,
          },
        ],
      }));
      gameStake = wager * allUsers.length;
      break;
    }
    case MatchType.BirdiePoolGross:
    case MatchType.BirdiePoolNet: {
      const allUserLength = allUsers?.length;
      const scoreType =
        matchType === MatchType.BirdiePoolGross ? "grossToPar" : "netToPar";
      const count = allUsers.reduce((birdieCount, user, userIndex) => {
        const winCount = user.holes.reduce(
          (prev, hole) => (hole[scoreType] < 0 ? prev + 1 : prev),
          0,
        );
        allUsers[userIndex].winCount = winCount;
        return birdieCount + winCount;
      }, 0);
      const wager = count * stake;
      for (let i = 17; i > -1; i -= 1) {
        const winners = allUsers.filter((u) => u.holes[i][scoreType] < 0);
        if (winners.length) {
          for (let j = 0; j < allUsers?.length; j += 1) {
            let win = 0;
            if (allUsers[j].holes[i][scoreType] < 0) {
              win = (wager * allUserLength) / winners.length;
            }
            allUsers[j].win = win;
          }
          break;
        }
      }
      allUsers = allUsers.map((u) => ({
        ...u,
        wager,
        net: Math.round(u.win - wager),
        bets: [
          ...(u.bets || []),
          {
            net: Math.round(u.win - wager),
            win: u.win,
            stake,
          },
        ],
      }));
      gameStake = wager * allUsers.length;
      break;
    }
    case MatchType.MatchPlayGross:
    case MatchType.MatchPlayNet: {
      if (matchType === MatchType.MatchPlayNet) {
        allUsers = allUsers.map((player) => ({
          ...player,
          holes: player.holes.map((h) => ({
            ...h,
            ...(h.score !== undefined
              ? {
                  netScore: calculateNetScore(
                    player.matchHandicap,
                    h.handicap,
                    h.score,
                  ),
                }
              : {}),
          }))
        }));
      }
      const scoreType =
        matchType === MatchType.MatchPlayGross ? "score" : "netScore";
      for (let i = 0; i < allUsers.length; i += 1) {
        allUsers[i].matches = [];
        allUsers[i].ways = 0;
        allUsers[i].net = 0;
        for (let j = 0; j < allUsers.length; j += 1) {
          if (allUsers[i].phone !== allUsers[j].phone) {
            let match = {
              phone: allUsers[j].phone,
              displayName: allUsers[j].displayName,
              uid: allUsers[j].uid,
              holes: [],
            };
            for (let k = 0; k < 18; k += 1) {
              if (
                allUsers[i].holes[k][scoreType] <
                allUsers[j].holes[k][scoreType]
              ) {
                match.holes.push({ holeResult: "W" });
              } else if (
                allUsers[i].holes[k][scoreType] >
                allUsers[j].holes[k][scoreType]
              ) {
                match.holes.push({ holeResult: "L" });
              } else {
                match.holes.push({ holeResult: "T" });
              }
            }
            const { result: back9 } = calculateMatchPlay(
              match.holes,
              MatchPlayType.Back9,
              pressLimit,
              pressType,
            );
            const { result: front9 } = calculateMatchPlay(
              match.holes,
              MatchPlayType.Front9,
              pressLimit,
              pressType,
            );
            const { result: big18 } = calculateMatchPlay(
              match.holes,
              MatchPlayType.Big18,
              pressLimit,
              pressType,
            );
            match = { ...match, back9, front9, big18 };
            allUsers[i].matches.push(match);
            const ways = back9.score + front9.score + big18.score;
            const net = ways * stake;
            allUsers[i].ways += ways;
            allUsers[i].net += net;
          }
        }
        allUsers[i].bets = [{ net: allUsers[i].net, stake }];
      }
      break;
    }
    default:
      break;
  }

  if (
    [
      MatchType.StrokePlayGross,
      MatchType.StrokePlayNet,
      MatchType.Quota,
    ].includes(matchType)
  ) {
    allUsers = calculatePlaces(
      matchType,
      stake,
      allUsers,
      firstPlace,
      secondPlace,
    );
    gameStake = stake * allUsers.length;
  }

  const netSum = allUsers.reduce((acc, u) => acc + u.net, 0);
  if (netSum < 0) {
    const u = [...allUsers];
    u.sort((a, b) => b.net - a.net || a.holes[0].score - b.holes[0].score);
    const winner = allUsers.find((x) => x.phone === u[0].phone);
    winner.net += Math.abs(netSum);
  } else if (netSum > 0) {
    const u = [...allUsers];
    u.sort((a, b) => a.net - b.net || b.holes[0].score - a.holes[0].score);
    const looser = allUsers.find((x) => x.phone === u[0].phone);
    looser.net -= netSum;
  }

  return { allUsers, skins, gameStake };
}

export {
  isUserInTeeTimes,
  isUserInTeeTimeUsers,
  findUserIndexInTeeTimeUsers,
  isTeeTimeActive,
  findTeeTimeByUser,
  calculatePlaces,
  findUserIndexInTeams,
  calculatePayouts,
  calculateMatchesForIndividuals,
  calculateMatchPlay,
  calculateMatchPlayForSingleTeeTime,
};
